import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { FieldSelector, type FieldDef } from '../components/FieldSelector.js';
import { SchedulePicker, type ScheduleType } from '../components/SchedulePicker.js';

// --- Types ---

interface DataSource {
  id: string;
  name: string;
  description: string;
  category: string;
  fields: FieldDef[];
  syncModes: string[];
  defaultSyncMode: string;
  defaultFields: string[];
}

interface SheetConnection {
  id: string;
  name: string;
  serviceAccountEmail: string;
}

interface Account {
  id: string;
  name: string;
  region: string;
}

interface ExportFilters {
  dateRange?: { start: string; end: string };
  marketplaceIds?: string[];
  skus?: string[];
  asins?: string[];
}

const STEPS = [
  { key: 'source', label: 'Data Source' },
  { key: 'fields', label: 'Fields' },
  { key: 'filters', label: 'Filters' },
  { key: 'destination', label: 'Destination' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'ai', label: 'AI Enrichment' },
] as const;

type StepKey = (typeof STEPS)[number]['key'];

const SOURCE_CATEGORIES: Record<string, string> = {
  Orders: 'bg-blue-100 text-blue-700',
  Inventory: 'bg-amber-100 text-amber-700',
  Listings: 'bg-purple-100 text-purple-700',
  Financial: 'bg-green-100 text-green-700',
  Shipping: 'bg-orange-100 text-orange-700',
  Performance: 'bg-pink-100 text-pink-700',
  Advertising: 'bg-red-100 text-red-700',
};

const MARKETPLACES: { id: string; name: string; region: string }[] = [
  { id: 'ATVPDKIKX0DER', name: 'US', region: 'NA' },
  { id: 'A2EUQ1WTGCTBG2', name: 'CA', region: 'NA' },
  { id: 'A1AM78C64UM0Y8', name: 'MX', region: 'NA' },
  { id: 'A1F83G8C2ARO7P', name: 'UK', region: 'EU' },
  { id: 'A1PA6795UKMFR9', name: 'DE', region: 'EU' },
  { id: 'A13V1IB3VIYZZH', name: 'FR', region: 'EU' },
  { id: 'APJ6JRA9NG5V4', name: 'IT', region: 'EU' },
  { id: 'A1RKKUPIHCS9HS', name: 'ES', region: 'EU' },
  { id: 'A1805IZSGTT6HS', name: 'NL', region: 'EU' },
  { id: 'A2NODRKZP88ZB9', name: 'SE', region: 'EU' },
  { id: 'A1C3SOZRARQ6R3', name: 'PL', region: 'EU' },
];

// --- Component ---

export function ExportEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  // Data
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [connections, setConnections] = useState<SheetConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Current step
  const [activeStep, setActiveStep] = useState<StepKey>('source');

  // Form state
  const [name, setName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [filters, setFilters] = useState<ExportFilters>({});
  const [connectionId, setConnectionId] = useState('');
  const [sheetUrl, setSheetUrl] = useState('');
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [sheetName, setSheetName] = useState('');
  const [sheetTabs, setSheetTabs] = useState<string[]>([]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [syncMode, setSyncMode] = useState<'full_refresh' | 'append'>('full_refresh');
  const [schedule, setSchedule] = useState<ScheduleType>('daily');
  const [scheduleHour, setScheduleHour] = useState(6);
  const [scheduleDow, setScheduleDow] = useState(1);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiEnrichments, setAiEnrichments] = useState<string[]>([]);

  // Source category filter
  const [sourceFilter, setSourceFilter] = useState('');

  // Load initial data
  useEffect(() => {
    async function load() {
      try {
        const [sourcesRes, connsRes, accts] = await Promise.all([
          api.get<{ dataSources: DataSource[] }>('/data-sources'),
          api.get<{ connections: SheetConnection[] }>('/sheets'),
          api.get<Account[]>('/accounts'),
        ]);
        // Map API field format { id, name } → FieldSelector format { key, label }
        const mapped = sourcesRes.dataSources.map((ds) => ({
          ...ds,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fields: ds.fields.map((f: any) => ({
            key: f.key ?? f.id,
            label: f.label ?? f.name,
            description: f.description,
            type: f.type,
          })),
        }));
        setDataSources(mapped);
        setConnections(connsRes.connections);

        if (accts.length > 0) setAccountId(accts[0].id);
        if (connsRes.connections.length > 0) setConnectionId(connsRes.connections[0].id);

        // If editing, load existing export
        if (id) {
          const exp = await api.get<Record<string, unknown>>(`/exports/${id}`);
          setName(exp.name as string);
          setAccountId(exp.accountId as string);
          setSelectedSource(exp.dataSource as string);
          setSelectedFields((exp.fields as string[]) ?? []);
          // Normalize filters from old format (date_from/date_to/marketplaces) to canonical format
          const rawFilters = (exp.filters ?? {}) as Record<string, unknown>;
          const normalized: ExportFilters = {};
          if (rawFilters.dateRange) {
            normalized.dateRange = rawFilters.dateRange as { start: string; end: string };
          } else if (rawFilters.date_from || rawFilters.date_to) {
            normalized.dateRange = {
              start: (rawFilters.date_from as string) || '',
              end: (rawFilters.date_to as string) || '',
            };
          }
          if (rawFilters.marketplaceIds) {
            normalized.marketplaceIds = rawFilters.marketplaceIds as string[];
          } else if (rawFilters.marketplaces) {
            normalized.marketplaceIds = rawFilters.marketplaces as string[];
          }
          if (rawFilters.skus) {
            normalized.skus = Array.isArray(rawFilters.skus)
              ? rawFilters.skus as string[]
              : (rawFilters.skus as string).split(',').map((s: string) => s.trim()).filter(Boolean);
          }
          if (rawFilters.asins) {
            normalized.asins = Array.isArray(rawFilters.asins)
              ? rawFilters.asins as string[]
              : (rawFilters.asins as string).split(',').map((s: string) => s.trim()).filter(Boolean);
          }
          setFilters(normalized);
          setConnectionId(exp.googleConnectionId as string);
          setSpreadsheetId(exp.spreadsheetId as string);
          setSheetName(exp.sheetName as string);
          setSyncMode((exp.syncMode as 'full_refresh' | 'append') ?? 'full_refresh');
          setSchedule((exp.scheduleType as ScheduleType) ?? 'daily');
          setScheduleHour((exp.scheduleHour as number) ?? 6);
          setScheduleDow((exp.scheduleDay as number) ?? 1);
          setAiEnabled(exp.aiEnabled as boolean);
          setAiEnrichments((exp.aiEnrichments as string[]) ?? []);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  // Derived
  const currentSource = dataSources.find((s) => s.id === selectedSource);
  const currentStepIndex = STEPS.findIndex((s) => s.key === activeStep);

  const categories = [...new Set(dataSources.map((s) => s.category))].sort();
  const filteredSources = sourceFilter
    ? dataSources.filter((s) => s.category === sourceFilter)
    : dataSources;

  // Parse Google Sheets URL or raw ID and auto-load tabs
  async function handleSheetInput(value: string) {
    setSheetUrl(value);
    const trimmed = value.trim();
    const match = trimmed.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
    const parsedId = match ? match[1] : (!trimmed.includes('/') ? trimmed : '');

    if (!parsedId || parsedId.length < 10) {
      setSpreadsheetId('');
      setSheetTabs([]);
      return;
    }

    setSpreadsheetId(parsedId);

    if (connectionId) {
      setLoadingTabs(true);
      try {
        const result = await api.get<{ tabs: string[] }>(
          `/sheets/${connectionId}/spreadsheet-tabs?spreadsheet_id=${encodeURIComponent(parsedId)}`,
        );
        const tabList = result.tabs ?? [];
        setSheetTabs(tabList);
        if (tabList.length > 0 && !sheetName) {
          setSheetName(tabList[0]);
        }
      } catch {
        setSheetTabs([]);
      } finally {
        setLoadingTabs(false);
      }
    }
  }

  // Select a source
  function handleSelectSource(sourceId: string) {
    const source = dataSources.find((s) => s.id === sourceId);
    setSelectedSource(sourceId);
    if (source) {
      setSelectedFields(source.fields.map((f) => f.key));
      if (!name) setName(`${source.name} Export`);
      if (!sheetName) setSheetName(source.name);
    }
  }

  // Navigation
  function goNext() {
    const idx = STEPS.findIndex((s) => s.key === activeStep);
    if (idx < STEPS.length - 1) {
      setActiveStep(STEPS[idx + 1].key);
    }
  }

  function goPrev() {
    const idx = STEPS.findIndex((s) => s.key === activeStep);
    if (idx > 0) {
      setActiveStep(STEPS[idx - 1].key);
    }
  }

  // Save
  async function handleSave() {
    if (!name.trim() || !selectedSource || !connectionId || !spreadsheetId.trim()) {
      setError('Please fill in all required fields: name, data source, connection, and spreadsheet ID.');
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      name: name.trim(),
      accountId,
      dataSource: selectedSource,
      fields: selectedFields,
      fieldOrder: selectedFields,
      filters,
      googleConnectionId: connectionId,
      spreadsheetId: spreadsheetId.trim(),
      sheetName: sheetName.trim() || currentSource?.name || 'Sheet1',
      syncMode,
      scheduleType: schedule,
      scheduleHour,
      scheduleDay: scheduleDow,
      aiEnabled,
      aiEnrichments,
    };

    try {
      if (isEditing) {
        await api.put(`/exports/${id}`, payload);
      } else {
        await api.post('/exports', payload);
      }
      navigate('/sheets?tab=exports');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save export');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/sheets?tab=exports" className="text-sm text-brand-600 hover:text-brand-700">
          &larr; Back to Sheets Hub
        </Link>
        <h2 className="mt-2 text-base font-semibold">{isEditing ? 'Edit Export' : 'New Export'}</h2>
        <p className="text-sm text-gray-500">Configure your data export pipeline step by step.</p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Export name */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <label className="block text-xs font-medium text-gray-700">Export Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          placeholder="e.g., Daily Orders Export"
        />
      </div>

      {/* Step progress indicator */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {STEPS.map((step, i) => {
          const isCurrent = activeStep === step.key;
          const isPast = i < currentStepIndex;
          return (
            <button
              key={step.key}
              onClick={() => setActiveStep(step.key)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                isCurrent
                  ? 'bg-brand-600 text-white'
                  : isPast
                    ? 'bg-brand-100 text-brand-700'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                  isCurrent
                    ? 'bg-white/20 text-white'
                    : isPast
                      ? 'bg-brand-200 text-brand-800'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {isPast ? (
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : (
                  i + 1
                )}
              </span>
              {step.label}
            </button>
          );
        })}
      </div>

      {/* Step content */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        {/* Step 1: Source */}
        {activeStep === 'source' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold">Choose Data Source</h3>
              <p className="mt-1 text-xs text-gray-500">Select what data you want to export.</p>
            </div>

            {/* Category filter */}
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setSourceFilter('')}
                className={`rounded-lg px-3 py-1 text-xs font-medium ${
                  !sourceFilter ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSourceFilter(cat)}
                  className={`rounded-lg px-3 py-1 text-xs font-medium ${
                    sourceFilter === cat ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Source list */}
            <div className="grid gap-2 sm:grid-cols-2">
              {filteredSources.map((source) => (
                <button
                  key={source.id}
                  type="button"
                  onClick={() => handleSelectSource(source.id)}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    selectedSource === source.id
                      ? 'border-brand-300 bg-brand-50 ring-1 ring-brand-300'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium ${selectedSource === source.id ? 'text-brand-700' : 'text-gray-900'}`}>
                      {source.name}
                    </p>
                    <span className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${SOURCE_CATEGORIES[source.category] ?? 'bg-gray-100 text-gray-600'}`}>
                      {source.category}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{source.description}</p>
                </button>
              ))}
              {filteredSources.length === 0 && (
                <p className="col-span-2 text-sm text-gray-500 text-center py-8">No data sources available.</p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Fields */}
        {activeStep === 'fields' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold">Select Fields</h3>
              <p className="mt-1 text-xs text-gray-500">
                Choose which fields to include in your export
                {currentSource ? ` from ${currentSource.name}` : ''}.
              </p>
            </div>

            {currentSource ? (
              <FieldSelector
                available={currentSource.fields}
                selected={selectedFields}
                onChange={setSelectedFields}
              />
            ) : (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <p className="text-sm text-yellow-700">Please select a data source first.</p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Filters */}
        {activeStep === 'filters' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold">Configure Filters</h3>
              <p className="mt-1 text-xs text-gray-500">Narrow down the data to export. All filters are optional.</p>
            </div>

            {/* Date range */}
            {(
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-700">Date Range</label>
                <div className="flex flex-wrap gap-3">
                  <div>
                    <label className="block text-xs text-gray-500">From</label>
                    <input
                      type="date"
                      value={filters.dateRange?.start ?? ''}
                      onChange={(e) => setFilters((f) => {
                        const val = e.target.value;
                        if (!val && !f.dateRange?.end) return { ...f, dateRange: undefined };
                        return { ...f, dateRange: { start: val || '', end: f.dateRange?.end || '' } };
                      })}
                      className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">To</label>
                    <input
                      type="date"
                      value={filters.dateRange?.end ?? ''}
                      onChange={(e) => setFilters((f) => {
                        const val = e.target.value;
                        if (!val && !f.dateRange?.start) return { ...f, dateRange: undefined };
                        return { ...f, dateRange: { start: f.dateRange?.start || '', end: val || '' } };
                      })}
                      className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Marketplace filter */}
            {(
              <div className="space-y-3">
                <label className="block text-xs font-medium text-gray-700">Marketplaces</label>
                {(['NA', 'EU'] as const).map((region) => {
                  const regionMps = MARKETPLACES.filter((mp) => mp.region === region);
                  return (
                    <div key={region}>
                      <p className="mb-1 text-xs font-medium text-gray-400 uppercase tracking-wider">
                        {region === 'NA' ? 'Americas' : 'Europe'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {regionMps.map((mp) => {
                          const sel = filters.marketplaceIds?.includes(mp.id) ?? false;
                          return (
                            <button
                              key={mp.id}
                              type="button"
                              onClick={() => {
                                setFilters((f) => {
                                  const current = f.marketplaceIds ?? [];
                                  const updated = sel
                                    ? current.filter((m) => m !== mp.id)
                                    : [...current, mp.id];
                                  return {
                                    ...f,
                                    marketplaceIds: updated.length > 0 ? updated : undefined,
                                  };
                                });
                              }}
                              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                                sel
                                  ? 'bg-brand-100 text-brand-700 ring-1 ring-brand-300'
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                            >
                              {mp.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {(filters.marketplaceIds?.length ?? 0) === 0 && (
                  <p className="text-xs text-gray-400">No marketplaces selected = all marketplaces included.</p>
                )}
              </div>
            )}

            {/* SKU filter */}
            {(
              <div>
                <label className="block text-xs font-medium text-gray-700">SKUs</label>
                <input
                  type="text"
                  value={filters.skus?.join(', ') ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    const arr = val ? val.split(',').map((s) => s.trim()).filter(Boolean) : undefined;
                    setFilters((f) => ({ ...f, skus: arr }));
                  }}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="Comma-separated: SKU-001, SKU-002"
                />
              </div>
            )}

            {/* ASIN filter */}
            {(
              <div>
                <label className="block text-xs font-medium text-gray-700">ASINs</label>
                <input
                  type="text"
                  value={filters.asins?.join(', ') ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    const arr = val ? val.split(',').map((s) => s.trim()).filter(Boolean) : undefined;
                    setFilters((f) => ({ ...f, asins: arr }));
                  }}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="Comma-separated: B08N5WRWNW, B09ABC1234"
                />
              </div>
            )}
          </div>
        )}

        {/* Step 4: Destination */}
        {activeStep === 'destination' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold">Configure Destination</h3>
              <p className="mt-1 text-xs text-gray-500">Set up where the data will be synced to.</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700">Google Connection</label>
              <select
                value={connectionId}
                onChange={(e) => setConnectionId(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {connections.length === 0 && <option value="">No connections -- add one in Sheets page</option>}
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.serviceAccountEmail})
                  </option>
                ))}
              </select>
              {connections.length === 0 && (
                <Link to="/sheets" className="mt-1 inline-block text-xs font-medium text-brand-600 hover:text-brand-700">
                  Add a Google Sheets connection first &rarr;
                </Link>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700">Google Sheets Link</label>
              <input
                type="text"
                value={sheetUrl || spreadsheetId}
                onChange={(e) => handleSheetInput(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Paste Google Sheets URL or spreadsheet ID"
              />
              {spreadsheetId && spreadsheetId !== (sheetUrl || spreadsheetId).trim() && (
                <p className="mt-1 text-xs text-gray-400">ID: {spreadsheetId.slice(0, 20)}...</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700">Sheet Tab</label>
              {loadingTabs ? (
                <p className="mt-1 text-xs text-gray-400">Loading tabs...</p>
              ) : sheetTabs.length > 0 ? (
                <select
                  value={sheetName}
                  onChange={(e) => setSheetName(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                >
                  {sheetTabs.map((tab) => (
                    <option key={tab} value={tab}>{tab}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={sheetName}
                  onChange={(e) => setSheetName(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="e.g., Orders"
                />
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700">Sync Mode</label>
              <div className="mt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => setSyncMode('full_refresh')}
                  className={`flex-1 rounded-lg border p-3 text-left transition-colors ${
                    syncMode === 'full_refresh'
                      ? 'border-brand-300 bg-brand-50 ring-1 ring-brand-300'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <p className={`text-sm font-medium ${syncMode === 'full_refresh' ? 'text-brand-700' : 'text-gray-900'}`}>
                    Full Refresh
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">Replace all data each sync. Best for current state data.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setSyncMode('append')}
                  className={`flex-1 rounded-lg border p-3 text-left transition-colors ${
                    syncMode === 'append'
                      ? 'border-brand-300 bg-brand-50 ring-1 ring-brand-300'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <p className={`text-sm font-medium ${syncMode === 'append' ? 'text-brand-700' : 'text-gray-900'}`}>
                    Append
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">Add new rows each sync. Best for historical/log data.</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Schedule */}
        {activeStep === 'schedule' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold">Set Schedule</h3>
              <p className="mt-1 text-xs text-gray-500">How often should this export sync?</p>
            </div>

            <SchedulePicker
              schedule={schedule}
              hour={scheduleHour}
              dayOfWeek={scheduleDow}
              onScheduleChange={setSchedule}
              onHourChange={setScheduleHour}
              onDayOfWeekChange={setScheduleDow}
            />
          </div>
        )}

        {/* Step 6: AI Enrichment */}
        {activeStep === 'ai' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold">AI Enrichment</h3>
              <p className="mt-1 text-xs text-gray-500">Optionally add AI-powered columns to your export.</p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Enable AI Enrichment</p>
                <p className="text-xs text-gray-500">Add AI-generated insights as extra columns.</p>
              </div>
              <button
                type="button"
                onClick={() => setAiEnabled(!aiEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  aiEnabled ? 'bg-brand-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    aiEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {aiEnabled && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm text-gray-500">
                  AI enrichment will add AI-generated insights as extra columns to your export.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step navigation */}
        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={goPrev}
            disabled={currentStepIndex === 0}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-30"
          >
            Previous
          </button>
          <div className="flex items-center gap-3">
            {currentStepIndex < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={goNext}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg bg-brand-600 px-6 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : isEditing ? 'Update Export' : 'Create Export'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold">Export Summary</h3>
        <div className="mt-3 grid gap-x-8 gap-y-2 sm:grid-cols-2">
          <SummaryRow label="Name" value={name || '--'} />
          <SummaryRow label="Data Source" value={currentSource?.name || '--'} />
          <SummaryRow label="Fields" value={selectedFields.length > 0 ? `${selectedFields.length} selected` : '--'} />
          <SummaryRow
            label="Filters"
            value={
              [
                filters.dateRange?.start && `From ${filters.dateRange.start}`,
                filters.dateRange?.end && `To ${filters.dateRange.end}`,
                (filters.marketplaceIds?.length ?? 0) > 0 && `${filters.marketplaceIds!.length} marketplaces`,
                filters.skus?.length && 'SKU filter',
                filters.asins?.length && 'ASIN filter',
              ]
                .filter(Boolean)
                .join(', ') || 'None'
            }
          />
          <SummaryRow
            label="Destination"
            value={
              connections.find((c) => c.id === connectionId)?.name
                ? `${connections.find((c) => c.id === connectionId)!.name} / ${sheetName || '--'}`
                : '--'
            }
          />
          <SummaryRow label="Sync Mode" value={syncMode === 'full_refresh' ? 'Full Refresh' : 'Append'} />
          <SummaryRow label="Schedule" value={schedule} />
          <SummaryRow label="AI Enrichment" value={aiEnabled ? `On (${aiEnrichments.length} enrichments)` : 'Off'} />
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs font-medium text-gray-500 w-28 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-900 truncate">{value}</span>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div>
        <div className="h-4 w-24 rounded bg-gray-200" />
        <div className="mt-3 h-5 w-32 rounded bg-gray-200" />
        <div className="mt-2 h-4 w-64 rounded bg-gray-200" />
      </div>
      <div className="h-16 rounded-xl bg-gray-200" />
      <div className="h-10 rounded-lg bg-gray-200" />
      <div className="h-80 rounded-xl bg-gray-200" />
      <div className="h-40 rounded-xl bg-gray-200" />
    </div>
  );
}
